import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { getf, sfLogoText } from './sectionHelpers';

interface LinkGroup {
  group_title: string;
  links: string | Array<string | { label: string; url: string }>;
}

interface SocialLink {
  platform: string;
  url: string;
}

// Variant colour schemes: [bg, text, accent]
const VARIANT_STYLES: Record<string, { bg: string; text: string; accent: string; border: string }> = {
  dark:    { bg: '#1e293b', text: 'rgba(255,255,255,0.7)', accent: '#fff', border: 'rgba(255,255,255,0.1)' },
  teal:    { bg: '#0f766e', text: 'rgba(255,255,255,0.8)', accent: '#fff', border: 'rgba(255,255,255,0.15)' },
  navy:    { bg: '#0f172a', text: 'rgba(255,255,255,0.7)', accent: '#38bdf8', border: 'rgba(255,255,255,0.1)' },
  light:   { bg: '#f8fafc', text: '#64748b', accent: '#1e293b', border: 'rgba(0,0,0,0.08)' },
};

const FooterStandard: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const variant = c.variant || section.variant || 'dark';
  const scheme = VARIANT_STYLES[variant] || VARIANT_STYLES.dark;

  const bg    = c.background_color || scheme.bg;
  const color = scheme.text;
  const headingColor = scheme.accent;
  const borderColor  = scheme.border;

  const linkGroups = (c.link_groups || []) as LinkGroup[];

  // Social links: Python supports both dict {platform: url} and array [{platform, url}]
  let socialLinks: SocialLink[] = [];
  if (Array.isArray(c.social_links)) {
    socialLinks = c.social_links as SocialLink[];
  } else if (c.social_links && typeof c.social_links === 'object') {
    socialLinks = Object.entries(c.social_links as Record<string, string>).map(
      ([platform, url]) => ({ platform, url })
    );
  }

  // copyright → copyright_text fallback
  const copyright = getf(c, 'copyright_text', 'copyright');
  const logoText  = sfLogoText(c, '');

  return (
    <>
      <div style={{ height: 4, background: `linear-gradient(90deg, var(--brand-primary, #1a365d), var(--brand-secondary, #2563eb))` }} />
      <footer className="sb-render sb-render-footer" style={{ background: bg, color, padding: '64px 24px 32px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `2fr ${linkGroups.map(() => '1fr').join(' ')}`.trim() || '1fr',
            gap: 48,
            marginBottom: 48,
          }}>
            {/* Brand column */}
            <div>
              {c.logo_url ? (
                <img src={c.logo_url} alt={logoText || 'Logo'} style={{ height: 32, marginBottom: 16 }} />
              ) : logoText ? (
                <div style={{ fontWeight: 800, fontSize: '1.25rem', color: headingColor, marginBottom: 16 }}>
                  {logoText}
                </div>
              ) : null}
              <InlineEditText tag="p" controlKey="tagline" style={{ maxWidth: 300, lineHeight: 1.6 }}>
                {c.tagline || 'Your company tagline here'}
              </InlineEditText>
              {socialLinks.length > 0 && (
                <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
                  {socialLinks.map((sl, i) => (
                    <a key={i} href={sl.url || '#'} style={{ color, textTransform: 'capitalize', textDecoration: 'none', fontWeight: 500 }}>
                      {sl.platform}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Link group columns */}
            {linkGroups.map((g, gi) => {
              // Python supports: comma-separated string, pipe-delimited "Label|url" strings, or object arrays
              let links: Array<{ label: string; url: string }> = [];
              if (typeof g.links === 'string') {
                links = g.links.split(',').map(l => {
                  const parts = l.trim().split('|');
                  return { label: parts[0]?.trim() || '', url: parts[1]?.trim() || '#' };
                });
              } else if (Array.isArray(g.links)) {
                links = g.links.map(l => {
                  if (typeof l === 'string') {
                    const parts = l.split('|');
                    return { label: parts[0]?.trim() || '', url: parts[1]?.trim() || '#' };
                  }
                  return { label: l.label || '', url: l.url || '#' };
                });
              }
              return (
                <div key={gi}>
                  <h4 style={{ color: headingColor, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>
                    {g.group_title}
                  </h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {links.map((lnk, li) => (
                      <li key={li} style={{ marginBottom: 8 }}>
                        <a href={lnk.url} style={{ color, textDecoration: 'none' }}>{lnk.label}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Contact row */}
          {(c.phone || c.email || c.address) && (
            <div style={{ marginBottom: 16, fontSize: '0.875rem' }}>
              {c.phone  && <span style={{ marginRight: 16 }}>{c.phone}</span>}
              {c.email  && <span style={{ marginRight: 16 }}>{c.email}</span>}
              {c.address && <span>{c.address}</span>}
            </div>
          )}

          {/* Bottom bar */}
          <div style={{ borderTop: `1px solid ${borderColor}`, paddingTop: 24, fontSize: '0.875rem' }}>
            <p style={{ margin: 0 }}>&copy; {copyright || new Date().getFullYear()}</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default FooterStandard;
